import { usStates } from "@/data/usStates";

/**
 * How an organization's footprint is shown on its profile: every office city
 * grouped under its state (the default), or the states only — for a cloud or
 * statewide brokerage where anyone licensed in the state can join.
 *
 * Stored as `profile_data.officesDisplay`, written through
 * save_org_profile_patch; the public RPC returns it as `offices_display`.
 * Spec: docs/superpowers/specs/2026-09-21-org-statewide-footprint-display-design.md
 */
export type OfficesDisplay = "cities" | "states";

/** Anything but exactly "states" reads as the default city list. */
export function parseOfficesDisplay(value: unknown): OfficesDisplay {
  return value === "states" ? "states" : "cities";
}

const STATE_NAMES: Record<string, string> = Object.fromEntries(usStates.map((s) => [s.value, s.label]));

/** "California" for "CA" (any case); the code itself when unknown; "" when blank. */
export function stateName(code: string | null | undefined): string {
  const c = (code ?? "").trim().toUpperCase();
  if (c === "") return "";
  return STATE_NAMES[c] ?? c;
}

/**
 * "City, ST" labels as the states they are in, first-seen order, each once.
 * A label with no state has nothing to name, so it is kept as it is.
 */
export function officeLabelsAsStates(labels: string[]): string[] {
  const out: string[] = [];
  for (const label of labels) {
    const comma = label.indexOf(",");
    const name = comma < 0 ? "" : stateName(label.slice(comma + 1));
    const value = name || label.trim();
    if (value !== "" && !out.includes(value)) out.push(value);
  }
  return out;
}
